import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import {
  PencilIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const OrganizationAnnouncementsList = ({ organizationId, isAdmin }) => {
  const [messages, setMessages] = useState([]);
  const [archivedMessages, setArchivedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [editMessage, setEditMessage] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', text: '', link: '', linkText: '' });
  const [saving, setSaving] = useState(false);

  const loadMessages = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const all = [];
      // Load from messages (web-created)
      try {
        const messagesRef = collection(db, 'organizations', organizationId, 'messages');
        const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(150));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          const data = d.data();
          all.push({
            id: d.id,
            _source: 'messages',
            title: data.title,
            text: data.text,
            message: data.message,
            link: data.link,
            linkText: data.linkText,
            imageUrl: data.imageUrl,
            archived: data.archived,
            createdAt: data.createdAt?.toDate?.() || new Date(),
          });
        });
      } catch (e) {
        console.warn('Error loading messages:', e);
      }
      // Load from announcements (mobile-created and shared list)
      try {
        const announcementsRef = collection(db, 'organizations', organizationId, 'announcements');
        const q = query(announcementsRef, orderBy('createdAt', 'desc'), limit(150));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          const data = d.data();
          all.push({
            id: d.id,
            _source: 'announcements',
            title: data.title,
            text: data.message,
            message: data.message,
            authorName: data.authorName,
            archived: data.archived,
            createdAt: data.createdAt?.toDate?.() || new Date(),
          });
        });
      } catch (e) {
        console.warn('Error loading announcements:', e);
      }
      all.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
      setMessages(all.filter((m) => m.archived !== true));
      setArchivedMessages(all.filter((m) => m.archived === true));
    } catch (err) {
      console.error('Error loading announcements:', err);
      toast.error('Failed to load announcements');
      setMessages([]);
      setArchivedMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [organizationId]);

  const collectionName = (msg) => (msg._source === 'announcements' ? 'announcements' : 'messages');

  const handleArchive = async (msg) => {
    if (!isAdmin) return;
    try {
      const ref = doc(db, 'organizations', organizationId, collectionName(msg), msg.id);
      await updateDoc(ref, { archived: true, archivedAt: serverTimestamp() });
      toast.success('Announcement archived');
      loadMessages();
    } catch (err) {
      console.error(err);
      toast.error('Failed to archive');
    }
  };

  const handleRestore = async (msg) => {
    if (!isAdmin) return;
    try {
      const ref = doc(db, 'organizations', organizationId, collectionName(msg), msg.id);
      await updateDoc(ref, { archived: false, archivedAt: deleteField() });
      toast.success('Announcement restored');
      loadMessages();
    } catch (err) {
      console.error(err);
      toast.error('Failed to restore');
    }
  };

  const handleDelete = async (msg) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete "${(msg.title || 'Untitled').slice(0, 50)}"? This cannot be undone.`)) return;
    try {
      const ref = doc(db, 'organizations', organizationId, collectionName(msg), msg.id);
      await deleteDoc(ref);
      toast.success('Announcement deleted');
      loadMessages();
      setEditMessage(null);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete');
    }
  };

  const openEdit = (msg) => {
    setEditMessage(msg);
    const body = msg.text ?? msg.message ?? '';
    setEditForm({
      title: msg.title || '',
      text: body,
      link: msg.link || '',
      linkText: msg.linkText || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editMessage || !organizationId) return;
    setSaving(true);
    try {
      const coll = collectionName(editMessage);
      const ref = doc(db, 'organizations', organizationId, coll, editMessage.id);
      const payload = {
        title: editForm.title.trim(),
        updatedAt: serverTimestamp(),
      };
      if (coll === 'messages') {
        payload.text = editForm.text.trim();
        payload.link = editForm.link.trim() || null;
        payload.linkText = editForm.linkText.trim() || null;
      } else {
        payload.message = editForm.text.trim();
      }
      await updateDoc(ref, payload);
      toast.success('Announcement updated');
      loadMessages();
      setEditMessage(null);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => (d && d.toLocaleDateString ? d.toLocaleDateString() : '');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3274B8]"></div>
      </div>
    );
  }

  const bodyText = (msg) => msg.text ?? msg.message ?? '';

  const renderCard = (msg, options = {}) => (
    <div
      key={`${msg._source}_${msg.id}`}
      className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{msg.title || 'Untitled'}</h3>
          {msg.authorName && <p className="text-xs text-gray-500 mb-1">By {msg.authorName}</p>}
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{bodyText(msg)}</p>
          {(msg.link || msg.imageUrl) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {msg.link && (
                <a
                  href={msg.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#3274B8] hover:underline"
                >
                  {msg.linkText || 'Link'}
                </a>
              )}
              {msg.imageUrl && (
                <img src={msg.imageUrl} alt="" className="max-h-24 rounded object-cover" />
              )}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">{formatDate(msg.createdAt)}</p>
        </div>
        {isAdmin && options.showActions !== false && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => handleArchive(msg)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Archive"
            >
              <ArchiveBoxIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => openEdit(msg)}
              className="p-2 text-gray-500 hover:text-[#3274B8] hover:bg-blue-50 rounded"
              title="Edit"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(msg)}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
              title="Delete"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {messages.length === 0 && archivedMessages.length === 0 ? (
        <p className="text-gray-500 py-6">No announcements yet. Create one above.</p>
      ) : (
        <>
          <div className="space-y-3">
            {messages.map((msg) => renderCard(msg))}
          </div>
          {archivedMessages.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowArchived(!showArchived)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium"
              >
                {showArchived ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ArchiveBoxIcon className="h-5 w-5" />
                )}
                {showArchived ? 'Hide archived' : `View archived (${archivedMessages.length})`}
              </button>
              {showArchived && (
                <div className="mt-3 space-y-3">
                  {archivedMessages.map((msg) => (
                    <div
                      key={`${msg._source}_${msg.id}`}
                      className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900">{msg.title || 'Untitled'}</h3>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-2">{bodyText(msg)}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(msg.createdAt)}</p>
                        </div>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => handleRestore(msg)}
                            className="flex items-center gap-1 shrink-0 text-sm text-[#3274B8] hover:underline font-medium"
                          >
                            <ArchiveBoxArrowDownIcon className="h-5 w-5" />
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Edit modal */}
      {editMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEditMessage(null)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Edit announcement</h3>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-[#3274B8] focus:border-[#3274B8]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={editForm.text}
                  onChange={(e) => setEditForm((f) => ({ ...f, text: e.target.value }))}
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-[#3274B8] focus:border-[#3274B8]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link (optional)</label>
                <input
                  type="url"
                  value={editForm.link}
                  onChange={(e) => setEditForm((f) => ({ ...f, link: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-[#3274B8] focus:border-[#3274B8]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link text (optional)</label>
                <input
                  type="text"
                  value={editForm.linkText}
                  onChange={(e) => setEditForm((f) => ({ ...f, linkText: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-[#3274B8] focus:border-[#3274B8]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditMessage(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#3274B8] text-white rounded-lg hover:bg-[#2861A1] disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationAnnouncementsList;
